import React from "react";
import classNames from "classnames";
import compose from "recompose/compose";
import { withStyles } from "@mui/styles";
import { withSnackbar } from "notistack";
import PropTypes from "prop-types";
import {
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { GetAgentDetail, UpdateAgentDetailsAPI } from "services/agent";
import {
  GetBrokerageAddressDetails,
  GetManagingBrokerDetails,
} from "services/broker";
import { showErrorNotification, showSuccessNotification } from "common/helpers";
import {
  OFFICE_NOT_LISTED,
  MANAGING_BROKER_NOT_LISTED,
  OFFICE_NOT_LISTED_LABEL,
  MANAGING_BROKER_NOT_LISTED_LABEL,
  formatAddressLine,
  getOfficeOptionName,
  isManagingBrokerNotListed,
  isOfficeNotListed,
  mapOfficeToBrokerageFields,
} from "views/AgentDetails/utils/brokerage";
import { createFreshdeskTicketTasks } from "views/AgentDetails/utils/freshDesk";
import { dismissOfficeConfirmationModal, useBrokerageOfficeDialog } from "views/Home/utils";
import styles from "./styles";

const DropdownOptionLabel = ({ classes, primary, secondary }) => (
  <div>
    <Typography variant="body2" className={classes.dropdownOptionPrimary}>
      {primary}
    </Typography>
    {secondary ? (
      <Typography variant="caption" className={classes.dropdownOptionSecondary}>
        {secondary}
      </Typography>
    ) : null}
  </div>
);

const BrokerageOfficeDialog = ({
  classes,
  enqueueSnackbar,
}) => {
  const { open, onClose, accountName, listgenUserID } =
    useBrokerageOfficeDialog();

  const [agentData, setAgentData] = React.useState({});
  const [values, setValues] = React.useState({});
  const [brokerageOfficeOptions, setBrokerageOfficeOptions] = React.useState([]);
  const [managingBrokerOptions, setManagingBrokerOptions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOfficeDataLoading, setIsOfficeDataLoading] = React.useState(false);
  const [isManagingBrokerDataLoading, setIsManagingBrokerDataLoading] =
    React.useState(false);
  const [officeDataError, setOfficeDataError] = React.useState(null);
  const [managingBrokerDataError, setManagingBrokerDataError] =
    React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});

  const notificationProps = React.useMemo(
    () => ({ enqueueSnackbar }),
    [enqueueSnackbar]
  );

  const loadBrokerageOptions = React.useCallback(
    async (nextAgentData) => {
      const countyID = nextAgentData && nextAgentData.countyID;
      const brokerageName = nextAgentData && nextAgentData.brokerageName;
      if (!countyID || !brokerageName) {
        setBrokerageOfficeOptions([]);
        setManagingBrokerOptions([]);
        return;
      }

      setIsOfficeDataLoading(true);
      setIsManagingBrokerDataLoading(true);
      setOfficeDataError(null);
      setManagingBrokerDataError(null);

      try {
        const [offices, brokers] = await Promise.all([
          GetBrokerageAddressDetails(countyID, brokerageName),
          GetManagingBrokerDetails(countyID, brokerageName),
        ]);

        const officeList = offices || [];
        const brokerList = brokers || [];
        setBrokerageOfficeOptions(officeList);
        setManagingBrokerOptions(brokerList);

        const {
          brokerageOfficeID,
          managingBrokerID,
          managingBrokerName,
          brokerLicenseNumber,
          brokerageAddressLine1,
          brokerageAddressLine2,
          brokerageCityID,
          brokerageCityName,
          brokerageCityCode,
          brokerageStateCode,
          brokerageAddressZip,
        } = nextAgentData;

        setValues({
          brokerageOfficeID,
          managingBrokerID,
          managingBrokerName,
          brokerLicenseNumber: brokerLicenseNumber || "",
          brokerageAddressLine1,
          brokerageAddressLine2,
          brokerageCityID,
          brokerageCityName,
          brokerageCityCode,
          brokerageStateCode,
          brokerageAddressZip,
        });
      } catch (err) {
        const message = err || "Unable to load brokerage details.";
        showErrorNotification(notificationProps, message);
        setOfficeDataError(message);
        setManagingBrokerDataError(message);
        setBrokerageOfficeOptions([]);
        setManagingBrokerOptions([]);
      } finally {
        setIsOfficeDataLoading(false);
        setIsManagingBrokerDataLoading(false);
      }
    },
    [notificationProps]
  );

  const loadAgentData = React.useCallback(async () => {
    if (!listgenUserID) return;

    setIsLoading(true);
    setSubmitted(false);
    setFieldErrors({});

    try {
      const data = await GetAgentDetail(listgenUserID);
      const nextAgentData = data && Object.keys(data).length ? data : {};
      setAgentData(nextAgentData);
      await loadBrokerageOptions(nextAgentData);
    } catch (err) {
      showErrorNotification(
        notificationProps,
        err || "Unable to load agent details."
      );
      setAgentData({});
      setValues({});
      setBrokerageOfficeOptions([]);
      setManagingBrokerOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [listgenUserID, loadBrokerageOptions, notificationProps]);

  React.useEffect(() => {
    if (!open) return;
    loadAgentData();
  }, [open, loadAgentData]);

  React.useEffect(() => {
    if (!submitted) return undefined;
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    return () => clearTimeout(timer);
  }, [submitted, onClose]);

  const showOffice =
    values.brokerageOfficeID &&
    !isOfficeNotListed(values.brokerageOfficeID);

  const handleOfficeChange = (event) => {
    const { value } = event.target;

    if (isOfficeNotListed(value)) {
      setValues((prevValues) => ({
        ...prevValues,
        brokerageOfficeID: OFFICE_NOT_LISTED,
      }));
      return;
    }

    const office = brokerageOfficeOptions.find(
      (item) => String(item.officeID) === String(value)
    );

    setValues((prevValues) => ({
      ...prevValues,
      ...(office ? mapOfficeToBrokerageFields(office) : { brokerageOfficeID: value }),
    }));
  };

  const handleManagingBrokerChange = (event) => {
    const { value } = event.target;

    if (isManagingBrokerNotListed(value)) {
      setValues((prevValues) => ({
        ...prevValues,
        managingBrokerID: MANAGING_BROKER_NOT_LISTED,
        managingBrokerName: MANAGING_BROKER_NOT_LISTED,
      }));
      return;
    }

    const broker = managingBrokerOptions.find(
      (item) => String(item.managingBrokerID) === String(value)
    );

    setValues((prevValues) => ({
      ...prevValues,
      managingBrokerID: value,
      managingBrokerName: broker ? broker.managingBrokerName : "",
    }));
  };

  const handleBrokerLicenseChange = (event) => {
    setValues((prevValues) => ({
      ...prevValues,
      brokerLicenseNumber: event.target.value,
    }));
  };

  const validateBrokerageFields = () => {
    const nextFieldErrors = {};

    if (!values.brokerageOfficeID) {
      nextFieldErrors.brokerageOfficeID = ["Office location is required."];
    }

    if (
      values.brokerageOfficeID &&
      !isOfficeNotListed(values.brokerageOfficeID) &&
      !values.brokerLicenseNumber
    ) {
      nextFieldErrors.brokerLicenseNumber = [
        "Brokerage license number is required for the selected office.",
      ];
    }

    if (!values.managingBrokerID) {
      nextFieldErrors.managingBrokerID = ["Managing broker is required."];
    }

    setFieldErrors(nextFieldErrors);
    return Object.keys(nextFieldErrors).length === 0;
  };

  const renderOfficeValue = (selected) => {
    if (!selected) return "";
    if (isOfficeNotListed(selected)) {
      return (
        <Typography variant="body2" className={classes.notListedOption}>
          {OFFICE_NOT_LISTED_LABEL}
        </Typography>
      );
    }
    const office = brokerageOfficeOptions.find(
      (item) => String(item.officeID) === String(selected)
    );
    if (!office) return "";
    return (
      <DropdownOptionLabel
        classes={classes}
        primary={getOfficeOptionName(office)}
        secondary={formatAddressLine(office)}
      />
    );
  };

  const renderManagingBrokerValue = (selected) => {
    if (!selected) return "";
    if (isManagingBrokerNotListed(selected)) {
      return (
        <Typography variant="body2" className={classes.notListedOption}>
          {MANAGING_BROKER_NOT_LISTED_LABEL}
        </Typography>
      );
    }
    const broker = managingBrokerOptions.find(
      (item) => String(item.managingBrokerID) === String(selected)
    );
    if (!broker) return "";
    return (
      <DropdownOptionLabel
        classes={classes}
        primary={broker.managingBrokerName}
        secondary={formatAddressLine(broker)}
      />
    );
  };

  const onSubmit = async () => {
    if (!validateBrokerageFields()) return;

    const ticketPromises = createFreshdeskTicketTasks({
      agentData,
      values,
      screenName: "Update Profile",
    });

    setIsSubmitting(true);

    try {
      if (ticketPromises.length) {
        await Promise.all(ticketPromises);
        showSuccessNotification(
          notificationProps,
          "Your request has been submitted. Our team will follow up shortly.",
          5000
        );
      }

      const result = await UpdateAgentDetailsAPI({
        ...agentData,
        ...values,
        brokerageCityCode: values.brokerageCityCode || "-",
        showOfficeConfirmationModal: false,
      });

      if (result !== true && result !== "true") {
        throw new Error("Unable to update brokerage details.");
      }

      dismissOfficeConfirmationModal();
      setSubmitted(true);
    } catch (err) {
      showErrorNotification(
        notificationProps,
        err && err.message
          ? err.message
          : "Unable to submit brokerage details. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MailProDialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
    >
      <MailProDialogHeader>
        <Typography variant="h6" className={classes.headerTitle}>
          Double checking your office location...
        </Typography>
      </MailProDialogHeader>
      <MailProDialogBody className={classes.dialogBody}>
        {isLoading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress size={32} />
          </div>
        ) : !submitted ? (
          <>
            <Typography variant="subtitle1" className={classes.greetingText}>
              Hi {accountName},
            </Typography>
            <Typography variant="body1" className={classes.descriptionText}>
              Thank you for helping us keep your office location up to date.
              <br/>
              Please confirm your brokerage office and managing broker:
            </Typography>

            <div className={classes.formFields}>
              <TextField
                select
                className={classes.textField}
                label="Select Your Brokerage Office Location"
                size="small"
                variant="outlined"
                required
                name="brokerageOfficeID"
                value={values.brokerageOfficeID || ""}
                error={Boolean(fieldErrors.brokerageOfficeID)}
                disabled={isOfficeDataLoading}
                helperText={
                  isOfficeDataLoading
                    ? "Loading office locations..."
                    : officeDataError
                    ? officeDataError
                    : !brokerageOfficeOptions.length && !isOfficeDataLoading
                    ? "No office locations found."
                    : ""
                }
                onChange={handleOfficeChange}
                SelectProps={{
                  renderValue: renderOfficeValue,
                  MenuProps: { className: classes.menu },
                }}
              >
                {brokerageOfficeOptions.map((office) => (
                  <MenuItem key={office.officeID} value={String(office.officeID)}>
                    <DropdownOptionLabel
                      classes={classes}
                      primary={getOfficeOptionName(office)}
                      secondary={formatAddressLine(office)}
                    />
                  </MenuItem>
                ))}
                <MenuItem value={OFFICE_NOT_LISTED}>
                  <Typography
                    variant="body2"
                    className={classNames(
                      classes.dropdownOptionPrimary,
                      classes.notListedOption
                    )}
                  >
                    {OFFICE_NOT_LISTED_LABEL}
                  </Typography>
                </MenuItem>
              </TextField>
              {fieldErrors.brokerageOfficeID && (
                <Typography className={classes.fieldError} variant="body2">
                  {fieldErrors.brokerageOfficeID[0]}
                </Typography>
              )}

              {showOffice && (
                <TextField
                  className={classes.textField}
                  label="Brokerage License Number"
                  size="small"
                  variant="outlined"
                  name="brokerLicenseNumber"
                  value={values.brokerLicenseNumber || ""}
                  error={Boolean(fieldErrors.brokerLicenseNumber)}
                  onChange={handleBrokerLicenseChange}
                />
              )}
              {fieldErrors.brokerLicenseNumber && (
                <Typography className={classes.fieldError} variant="body2">
                  {fieldErrors.brokerLicenseNumber[0]}
                </Typography>
              )}

              <TextField
                select
                className={classes.textField}
                label="Select Your Managing Broker"
                size="small"
                variant="outlined"
                required
                name="managingBrokerID"
                value={values.managingBrokerID || ""}
                error={Boolean(fieldErrors.managingBrokerID)}
                disabled={isManagingBrokerDataLoading}
                helperText={
                  isManagingBrokerDataLoading
                    ? "Loading managing brokers..."
                    : managingBrokerDataError
                    ? managingBrokerDataError
                    : !managingBrokerOptions.length &&
                      !isManagingBrokerDataLoading
                    ? "No managing brokers found."
                    : ""
                }
                onChange={handleManagingBrokerChange}
                SelectProps={{
                  renderValue: renderManagingBrokerValue,
                  MenuProps: { className: classes.menu },
                }}
              >
                {managingBrokerOptions.map((broker) => (
                  <MenuItem
                    key={broker.managingBrokerID}
                    value={String(broker.managingBrokerID)}
                  >
                    <DropdownOptionLabel
                      classes={classes}
                      primary={broker.managingBrokerName}
                      secondary={formatAddressLine(broker)}
                    />
                  </MenuItem>
                ))}
                <MenuItem value={MANAGING_BROKER_NOT_LISTED}>
                  <Typography
                    variant="body2"
                    className={classNames(
                      classes.dropdownOptionPrimary,
                      classes.notListedOption
                    )}
                  >
                    {MANAGING_BROKER_NOT_LISTED_LABEL}
                  </Typography>
                </MenuItem>
              </TextField>
              {fieldErrors.managingBrokerID && (
                <Typography className={classes.fieldError} variant="body2">
                  {fieldErrors.managingBrokerID[0]}
                </Typography>
              )}
            </div>

            <div className={classes.buttonRow}>
              <div className={classes.cancelButtonWrap}>
                <MailProButton
                  label="CANCEL"
                  variant="outlined"
                  onClick={onClose}
                  additionalClass={classes.cancelButton}
                  disabled={isSubmitting}
                />
              </div>
              <div className={classes.submitButtonWrap}>
                <MailProButton
                  label={isSubmitting ? "SUBMITTING..." : "SUBMIT"}
                  variant="filled"
                  onClick={onSubmit}
                  additionalClass={classes.submitButton}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={classes.successMessageWrap}>
            <Typography variant="h6" className={classes.successMessage}>
              Thank you! Your office has been updated.
            </Typography>
            <Typography variant="body2" className={classes.autoCloseHint}>
              This popup will close automatically in 5 seconds.
            </Typography>
          </div>
        )}
      </MailProDialogBody>
    </MailProDialog>
  );
};

BrokerageOfficeDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func,
};

export default compose(
  withStyles(styles),
  withSnackbar
)(BrokerageOfficeDialog);
